/**
 * UpgradeOverlay Component
 * 
 * Displays a blurred overlay with an upgrade prompt when user doesn't have
 * access to a specific service. Shows the actual content behind the blur
 * to give users a preview of what they're missing.
 * 
 * Usage:
 * <UpgradeOverlay service="performance_report">
 *   <PerformanceReportsContent />
 * </UpgradeOverlay>
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Lock, Crown, ArrowRight } from 'lucide-react';
import { useAccess } from '../hooks/useAccess';

/**
 * UpgradeOverlay - Shows blurred content with upgrade prompt
 * 
 * @param {Object} props
 * @param {string} props.service - Service name to check access for
 * @param {string} [props.title] - Override title (defaults to service display_name)
 * @param {string} [props.description] - Override description
 * @param {React.ReactNode} props.children - Content to show (blurred if no access)
 * @param {React.ReactNode} [props.previewContent] - Optional static preview content instead of children
 */
export function UpgradeOverlay({ 
  service, 
  title,
  description,
  children,
  previewContent 
}) {
  const { canAccess, getService, planName, planCategory } = useAccess();
  
  // If user has access, just render children normally
  if (canAccess(service)) {
    return children;
  }

  const serviceInfo = getService(service);
  const displayTitle = title || serviceInfo.display_name || 'Premium Feature';
  const displayDescription = description || serviceInfo.description || 'Upgrade your plan to access this feature.';
  
  // Determine the upgrade message based on required plan
  const getUpgradeMessage = () => {
    const requiredCategory = serviceInfo.required_plan_category;
    
    if (requiredCategory === 'stock_recommendations') {
      return 'This feature is available with Stock Recommendations plans (Silver, Silver+, Gold).';
    } else if (requiredCategory === 'live_corporate_announcements') {
      return 'This feature is available with Live ICA plans (Basic, Advance, Advance+).';
    } else if (serviceInfo.access_type === 'tiered') {
      const minTier = serviceInfo.min_tier;
      if (minTier) {
        return `This feature requires ${minTier.replace('_', ' ')} tier or higher.`;
      }
      return 'This feature requires an active Live ICA subscription.';
    }
    
    return 'Upgrade your subscription to access this feature.';
  };

  return (
    <div className="relative min-h-[400px]">
      {/* Blurred Background Content */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="filter blur-sm opacity-50 pointer-events-none select-none">
          {previewContent || children}
        </div>
      </div>
      
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/60 via-white/80 to-white/95 flex items-center justify-center p-4">
        {/* Upgrade Card */}
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 max-w-md w-full p-8 text-center transform transition-all">
          {/* Lock Icon */}
          <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
            <Lock className="w-10 h-10 text-white" />
          </div>
          
          {/* Title */}
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            {displayTitle}
          </h2>
          
          {/* Description */}
          <p className="text-gray-600 mb-4">
            {displayDescription}
          </p>
          
          {/* Upgrade Message */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6">
            <p className="text-blue-800 text-sm">
              {getUpgradeMessage()}
            </p>
          </div>
          
          {/* Current Plan Badge */}
          {planName && (
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-6">
              <Crown size={16} className="text-yellow-500" />
              <span>Current Plan: <strong>{planName}</strong></span>
            </div>
          )}
          
          {/* Upgrade Button */}
          <Link
            to="/pricing"
            className="inline-flex items-center justify-center gap-2 w-full px-6 py-4 bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 font-semibold rounded-xl hover:from-yellow-500 hover:to-yellow-600 transition-all shadow-md hover:shadow-lg"
          >
            <span>View Plans & Upgrade</span>
            <ArrowRight size={20} />
          </Link>
          
          {/* Help Text */}
          <p className="text-xs text-gray-400 mt-4">
            Questions? Contact us at support@climbcapital.in
          </p>
        </div>
      </div>
    </div>
  );
}

export default UpgradeOverlay;
